import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import { listBots, createBot } from '../../api/botsApi'; // Add createBot for inline creation
import { Button } from '../../components/ui/button';
import { Modal } from '../../components/ui/modal';
import { Input } from '../../components/ui/input';

const BotsListPage = () => {
  const [bots, setBots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const loadBots = async () => {
    try {
      setIsLoading(true);
      const data = await listBots();
      // API may return paginated response: { count, next, previous, results }
      const botsArray = data?.results || data;
      setBots(Array.isArray(botsArray) ? botsArray : []);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch bots:", err);
      setError(err.message || 'Failed to fetch bots. Please try again.');
      setBots([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBots();
  }, []);

  const handleOpenCreate = () => {
    setCreateError(null);
    setNewBotName('');
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    const name = newBotName.trim();
    if (!name) {
      setCreateError('Please enter a group name.');
      return;
    }
    try {
      setIsCreating(true);
      setCreateError(null);
      await createBot({ name });
      setShowCreateModal(false);
      setNewBotName('');
      // Refresh list
      await loadBots();
    } catch (err) {
      console.error('Failed to create bot group:', err);
      setCreateError(err?.detail || err?.message || 'Failed to create bot group.');
    } finally {
      setIsCreating(false);
    }
  };

  const createBotButton = (
    <Button variant="primary" onClick={handleOpenCreate} className="whitespace-nowrap text-sm sm:text-base">
      <span className="hidden sm:inline">Create New Bot Group</span>
      <span className="sm:hidden">+ New Bot</span>
    </Button>
  );

  return (
    <PageLayout title="Trading Bots" headerRightContent={createBotButton}>
      {isLoading && <p className="text-sm sm:text-base">Loading bots...</p>}
      {error && <p className="text-destructive text-sm sm:text-base">Error: {error}</p>}
      {!isLoading && !error && (
        <div>
          {bots.length === 0 ? (
            <p className="text-sm sm:text-base">No bots found. Get started by creating a new bot.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {bots.map((bot) => (
                <div key={bot.id} className="bg-backgroundSurface p-3 sm:p-4 rounded-lg shadow flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-primary mb-2 truncate">{bot.name}</h3>
                    <p className="text-sm"><strong>Strategy:</strong> {bot.strategy_template}</p>
                    <p className="text-sm"><strong>Status:</strong> {bot.is_active ? 'Active' : 'Inactive'}</p>
                  </div>
                  <div className="mt-3 sm:mt-4">
                    <Link to={`/bots/${bot.id}`}>
                      <Button variant="outline" size="sm" className="w-full">View Details</Button>
                    </Link>
                  </div>
                  {/* TODO: Add Edit/Delete buttons here if needed directly on the list page */}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <Modal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create New Bot Group"
          maxWidthClass="max-w-md"
          footer={[
            <Button key="cancel" variant="secondary" onClick={() => setShowCreateModal(false)} disabled={isCreating}>
              Cancel
            </Button>,
            <Button key="create" variant="primary" onClick={handleCreate} disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create'}
            </Button>,
          ]}
        >
          <div className="space-y-3">
            {createError && <div className="text-destructive text-sm">{createError}</div>}
            <label className="block text-sm font-medium text-textPrimary">Bot Group Name</label>
            <Input
              value={newBotName}
              onChange={(e) => setNewBotName(e.target.value)}
              placeholder="e.g., Mean Reversion Bots"
              autoFocus
            />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default BotsListPage;
